import * as React from 'react';

import isURL from 'validator/lib/isURL';

import initBem from 'js/lib/bem';

import { Textarea } from '@coursera/coursera-ui';

import PlagiarismDetectionContainer, {
  ASSIGNMENT_TYPES,
} from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionContainer';
import { getSimilarityCheckRoleFromAssignmentRole } from 'bundles/academic-integrity/components/plagiarism-detection/utils/SimilarityCheckRoles';
import type { AssignmentRole } from 'bundles/assignment-common/types/AssignmentRoles';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import UrlView from 'bundles/compound-assessments/components/form-parts/url/UrlView';
import { getInvalidUrlError, typeNames } from 'bundles/compound-assessments/constants';
import type { UrlPrompt, UrlResponse } from 'bundles/compound-assessments/types/FormParts';
import AcademicIntegrityExperiments from 'bundles/epic/clients/AcademicIntegrity';

import _t from 'i18n!nls/compound-assessments';

import 'css!./__styles__/UrlPart';

const bem = initBem('UrlPart');

type Props = {
  prompt: UrlPrompt;
  response: UrlResponse;
  onChangeResponse: (response: UrlResponse) => void;
  isDisabled: boolean;
  isAutoSaving: boolean;
  isReadOnly: boolean;
  showValidation: boolean;
  itemId: string;
  userId: number;
  courseId: string;
  responseId: string;
  role?: AssignmentRole;
  teamId?: string;
};

type State = {
  hasUnsavedChanges: boolean;
};

export const checkInvalid = (response: UrlResponse): FormPartsValidationStatus | null => {
  const { url } = response?.definition?.submissionPartResponse?.definition || {};
  return !url || url.trim().match(/^https?:\/\/$/) || !isURL(url) ? FormPartsValidationStatuses.warning : null;
};

class UrlPart extends React.Component<Props, State> {
  state = {
    hasUnsavedChanges: false,
  };

  onChange = (key: 'url' | 'title' | 'caption') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { response, onChangeResponse } = this.props;
    const { url, title, caption } = response?.definition?.submissionPartResponse?.definition;

    this.setState({ hasUnsavedChanges: true });

    onChangeResponse({
      typeName: typeNames.SUBMISSION_RESPONSE,
      definition: {
        submissionPartResponse: {
          typeName: 'urlResponse',
          definition: {
            url,
            title,
            caption,
            [key]: event.target.value,
          },
        },
      },
    });
  };

  onChangeUrl = this.onChange('url');

  onChangeTitle = this.onChange('title');

  onChangeCaption = this.onChange('caption');

  componentDidUpdate(prevProps: Props) {
    // Reset hasUnsavedChanges when manual saving is complete or when autosaving starts,
    // this will trigger an automatic similarity check if we are using v3.
    const { isDisabled, isAutoSaving } = this.props;
    const { hasUnsavedChanges } = this.state;
    if (hasUnsavedChanges && ((prevProps.isDisabled && !isDisabled) || (prevProps.isAutoSaving && !isAutoSaving))) {
      this.setState({ hasUnsavedChanges: false });
    }
  }

  render() {
    const {
      response,
      isDisabled,
      isReadOnly,
      showValidation,
      prompt: { id, plagiarismConfig },
      userId,
      courseId,
      itemId,
      responseId,
      role,
      teamId,
    } = this.props;
    const { url, title, caption } = response?.definition?.submissionPartResponse?.definition;

    const defaultPlagiarismDetectionOn = AcademicIntegrityExperiments.get('defaultPlagiarismDetectionEnabled');

    const isInvalid = !!(showValidation && checkInvalid(response));
    const isInvalidUrl = !!url && !isURL(url);

    return (
      <div className={bem()}>
        {isReadOnly ? (
          <div className={bem('readOnly')}>
            {/* TODO: UrlView renders YouTube, Vimeo, Image, Soundcloud embeds.
                It uses Backbone to render. Rewrite it to React. */}
            <UrlView submissionPart={{ url, title, caption }} />
          </div>
        ) : (
          <div>
            <div className={bem('input', { isInvalid })} data-testid="url-part-url">
              <Textarea
                value={url}
                disabled={isDisabled}
                label={_t('URL')}
                onChange={this.onChangeUrl}
                componentId={`text-input-url-${id}`}
                placeholder="https://example.com"
                rows={1}
                nativeHtmlAttributes={{
                  'aria-describedby': isInvalid ? `validation-error-${id}` : undefined,
                }}
              />
            </div>
            <div className={bem('input')} data-testid="url-part-title">
              <Textarea
                value={title}
                disabled={isDisabled}
                label={_t('Title')}
                onChange={this.onChangeTitle}
                componentId={`text-input-title-${id}`}
                rows={1}
              />
            </div>
            <div className={bem('input')} data-testid="url-part-caption">
              <Textarea
                value={caption}
                disabled={isDisabled}
                label={_t('Caption')}
                onChange={this.onChangeCaption}
                componentId={`text-input-caption-${id}`}
              />
            </div>
            {isInvalid && <ValidationError id={id} message={isInvalidUrl ? getInvalidUrlError() : null} />}
          </div>
        )}
        {plagiarismConfig?.plagiarismCheckEnable && defaultPlagiarismDetectionOn && (
          <PlagiarismDetectionContainer
            courseId={courseId}
            itemId={itemId}
            authorId={userId}
            assignmentType={ASSIGNMENT_TYPES.STAFF}
            responseId={responseId}
            hasContent={!!response.definition.submissionPartResponse?.definition?.url}
            hasUnsavedChanges={this.state.hasUnsavedChanges}
            role={getSimilarityCheckRoleFromAssignmentRole(role)}
            teamId={teamId}
          />
        )}
      </div>
    );
  }
}

export default UrlPart;
